import React, { ReactNode, useMemo } from 'react';
import { SVGIcon } from '../SVGIcon/SVGIcon';
import clsx from 'clsx';
import { IconName } from '../SVGIcon/SVGIcon.types';
import { APP_COLORS } from '@/config/colors';
import { BadgeRating } from '../Badge/Badge';

export interface CardSummaryBoxHSPProps {
  /**
   * Tytuł sekcji
   */
  title: string;
  /**
   * Ikona komponentu
   */
  icon: IconName;
  /**
   * Wartość do wyświetlenia (liczba, tekst lub element React)
   */
  value: string | number | ReactNode;
  /**
   * Opis sekcji
   */
  description: string;
  iconStatus?: boolean;
  /**
   * Dodatowe klasy CSS
   */
  variant?: 'default' | 'warning' | 'danger';
  label?: string;
  className?: string;
  /**
   * Opcjonalny URL dla linku "SPRAWDŹ"
   */
  link?: ReactNode;
}

/**
 * Uproszczona wersja CardSummaryBox dla wariantu HSP (kluczowe wskaźniki)
 * Wyświetla prostą wartość liczbową bez rating bars
 *
 * @example
 * ```tsx
 * <CardSummaryBoxHSP
 *   icon={<SVGIcon name="diagnostyka-ra" />}
 *   title="Liczba szkód"
 *   description="Liczba odnotowanych szkód i wartość największej naprawy"
 *   value={0}
 * />
 * ```
 */
export const CardSummaryBoxHSP: React.FC<CardSummaryBoxHSPProps> = ({
  title,
  variant = 'default',
  icon,
  value,
  iconStatus = false,
  description,
  label = '',
  className = '',
  link,
}) => {
  const iconBgVariantClass = useMemo(() => {
    if (variant === 'warning') return 'bg-yellow';
    if (variant === 'danger') return 'bg-red-200';
    return 'bg-grayBorder';
  }, [variant]);

  const iconVariantColor = useMemo(() => {
    if (variant === 'warning') return APP_COLORS.warning.iconColor;
    if (variant === 'danger') return APP_COLORS.red[800];
    return '#394553';
  }, [variant]);

  const rating = useMemo(() => {
    if (variant === 'danger') return 1;
    return 3;
  }, [variant]);

  return (
    <div
      className={`flex w-full flex-col items-start justify-between gap-5 rounded-2xl border border-solid border-[#EAECF6] bg-white px-5 py-5 lg:max-w-full lg:flex-row lg:py-7 ${className}`}
    >
      {/* Lewa strona - ikona, tytuł i opis */}
      <div className="flex flex-col flex-1 gap-4">
        <div className="flex gap-2 items-center">
          <div
            className={clsx(
              'flex justify-center items-center rounded-xl h-[40px] w-[40px] bg-grayBorder',
              iconBgVariantClass,
            )}
          >
            <SVGIcon name={icon} fill={iconVariantColor} />
          </div>
          <div className="flex-1 font-inter text-[18px] font-bold text-text-primary xl:text-[22px]">
            {title}
          </div>
        </div>
        <p className="mt-1 text-sm font-inter text-grayText">
          {description}
          </p>
      </div>

      {/* Prawa strona - wartość i link */}
      <div className="flex flex-col items-start mb-auto lg:items-end">
        {variant === 'danger' && !iconStatus && (
          <BadgeRating version="v2" badgeText={label} rating={rating} />
        )}
        {/* Wartość główna */}
        <div
          className={clsx(
            'flex flex-col gap-2 font-extrabold leading-1 text-text-primary',
            iconStatus
              ? 'mb-2 text-[16px] xl:text-[18px]'
              : 'text-[24px] xl:text-[32px]',
          )}
        >
          <div className="flex gap-2 items-center">
            {iconStatus && variant === 'warning' && (
              <svg
                width="54"
                height="51"
                viewBox="0 0 54 51"
                fill="none"
                xmlns="http://www.w3.org/2000/svg"
              >
                <path
                  fill-rule="evenodd"
                  clip-rule="evenodd"
                  d="M22.0359 8.29923C24.2143 4.43151 29.7871 4.43151 31.9627 8.29923L47.8524 36.5109C49.9881 40.3047 47.2459 44.9858 42.8919 44.9858H11.1096C6.75276 44.9858 4.01051 40.3047 6.14622 36.5109L22.0359 8.29923ZM29.8469 36.4569C29.8469 37.2111 29.5469 37.9345 29.0129 38.4678C28.4789 39.0011 27.7546 39.3008 26.9993 39.3008C26.2441 39.3008 25.5198 39.0011 24.9858 38.4678C24.4517 37.9345 24.1517 37.2111 24.1517 36.4569C24.1517 35.7026 24.4517 34.9792 24.9858 34.4459C25.5198 33.9126 26.2441 33.6129 26.9993 33.6129C27.7546 33.6129 28.4789 33.9126 29.0129 34.4459C29.5469 34.9792 29.8469 35.7026 29.8469 36.4569ZM26.9993 13.7055C26.2441 13.7055 25.5198 14.0051 24.9858 14.5385C24.4517 15.0718 24.1517 15.7952 24.1517 16.5494V25.0812C24.1517 25.8354 24.4517 26.5588 24.9858 27.0921C25.5198 27.6255 26.2441 27.9251 26.9993 27.9251C27.7546 27.9251 28.4789 27.6255 29.0129 27.0921C29.5469 26.5588 29.8469 25.8354 29.8469 25.0812V16.5494C29.8469 15.7952 29.5469 15.0718 29.0129 14.5385C28.4789 14.0051 27.7546 13.7055 26.9993 13.7055Z"
                  fill="#FFD91B"
                />
                <path
                  fill-rule="evenodd"
                  clip-rule="evenodd"
                  d="M6.14697 36.5107L22.0367 8.29907C24.2151 4.43134 29.7879 4.43134 31.9635 8.29907L47.8532 36.5107C49.9889 40.3045 47.2466 44.9856 42.8926 44.9856H11.1104C6.75351 44.9856 4.01126 40.3045 6.14697 36.5107ZM52.5573 33.8626C56.7234 41.263 51.3668 50.3839 42.8926 50.3839H11.1104C2.63562 50.3839 -2.72435 41.265 1.44287 33.8626L17.3331 5.64989C21.5742 -1.88002 32.4252 -1.88657 36.667 5.64989L36.6685 5.65252L52.5573 33.8626Z"
                  fill="#FFEE96"
                />
              </svg>
            )}
            {iconStatus && variant === 'danger' && (
              <>
                <svg
                  width="54"
                  height="54"
                  viewBox="0 0 54 54"
                  fill="none"
                  xmlns="http://www.w3.org/2000/svg"
                >
                  <circle
                    opacity="0.5"
                    cx="27"
                    cy="27"
                    r="27"
                    fill="url(#paint0_radial_2907_23746)"
                  />
                  <circle cx="27" cy="27" r="22" fill="#EE2A2A" />
                  <path
                    d="M37 20.3255L30.3255 27L37 33.6745L33.6745 37L27 30.3255L20.3255 37L17 33.6745L23.6745 27L17 20.3255L20.3255 17L27 23.6745L33.6745 17L37 20.3255Z"
                    fill="white"
                  />
                  <defs>
                    <radialGradient
                      id="paint0_radial_2907_23746"
                      cx="0"
                      cy="0"
                      r="1"
                      gradientUnits="userSpaceOnUse"
                      gradientTransform="translate(27 27) rotate(90) scale(27)"
                    >
                      <stop
                        offset="0.495225"
                        stop-color="#111438"
                        stop-opacity="0"
                      />
                      <stop offset="1" stop-color="#EE2A2A" />
                    </radialGradient>
                  </defs>
                </svg>
              </>
            )}
            {iconStatus && variant === 'default' && (
              <svg
                width="54"
                height="54"
                viewBox="0 0 54 54"
                fill="none"
                xmlns="http://www.w3.org/2000/svg"
              >
                <circle
                  opacity="0.5"
                  cx="27"
                  cy="27"
                  r="27"
                  fill="url(#paint0_radial_3072_25680)"
                />
                <circle cx="27.0008" cy="26.9939" r="21.7742" fill="#148B4D" />
                <path
                  d="M22.8039 36.5786L15.6797 29.5338L18.6733 26.5727L22.8039 30.6574L37.0731 16.5464L40.0668 19.5072L22.8039 36.5786Z"
                  fill="white"
                />
                <defs>
                  <radialGradient
                    id="paint0_radial_3072_25680"
                    cx="0"
                    cy="0"
                    r="1"
                    gradientUnits="userSpaceOnUse"
                    gradientTransform="translate(27 27) rotate(90) scale(27)"
                  >
                    <stop
                      offset="0.495225"
                      stop-color="#111438"
                      stop-opacity="0"
                    />
                    <stop offset="1" stop-color="#148B4D" />
                  </radialGradient>
                </defs>
              </svg>
            )}
            <div className="flex flex-col">
              {typeof value === 'string' || typeof value === 'number' ? (
                <span>{value}</span>
              ) : (
                value
              )}
              <div className="text-base font-medium text-text-primary">
                {link}
              </div>
            </div>
          </div>
        </div>
        {/* Link sprawdź */}
      </div>
    </div>
  );
};
